import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Save, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import adminClient from '@/lib/api/adminClient';

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [localSettings, setLocalSettings] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminClient.get('/admin/settings').then((r) => r.data),
  });

  useEffect(() => {
    if (data?.settings) {
      const initial: Record<string, string> = {};
      for (const [key, val] of Object.entries(data.settings)) {
        initial[key] = (val as any).value;
      }
      setLocalSettings(initial);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => adminClient.put('/admin/settings', { settings: localSettings }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setHasChanges(false);
    },
  });

  const updateSetting = (key: string, value: string) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const toggleSetting = (key: string) => {
    const current = localSettings[key];
    updateSetting(key, current === 'true' ? 'false' : 'true');
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">系統設定</h1>
        {hasChanges && (
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            儲存變更
          </Button>
        )}
      </div>

      {/* Feature Flags */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">功能開關</CardTitle>
          <CardDescription>控制全系統功能</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">維護模式</p>
              <p className="text-xs text-muted-foreground">停止非管理員用戶的存取</p>
            </div>
            <Switch
              checked={localSettings.maintenance_mode === 'true'}
              onCheckedChange={() => toggleSetting('maintenance_mode')}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">開放註冊</p>
              <p className="text-xs text-muted-foreground">允許新用戶註冊</p>
            </div>
            <Switch
              checked={localSettings.registration_open === 'true'}
              onCheckedChange={() => toggleSetting('registration_open')}
            />
          </div>
        </CardContent>
      </Card>

      {/* System Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">系統配置</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">最大併發回測數</label>
            <Input
              type="number"
              value={localSettings.max_concurrent_backtests || '3'}
              onChange={(e) => updateSetting('max_concurrent_backtests', e.target.value)}
              className="w-[120px]"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">預設試用點數</label>
            <Input
              type="number"
              value={localSettings.default_trial_credits || '100'}
              onChange={(e) => updateSetting('default_trial_credits', e.target.value)}
              className="w-[120px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">系統狀態</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>後端 API — 已連線</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
